import { BrowserRouter, Route } from 'react-router-dom'
import { CustomRoutes, Resource } from 'ra-core'
import { Admin } from '@internal/ui-kit/components/admin'
import { Loading } from '@internal/ui-kit/components/admin/loading'
import { dataProvider } from '@/providers/dataProvider'
import { authProvider } from '@/providers/authProvider'
import { SignInPage } from '@/pages/SignInPage'
import { RequestPasswordResetPage } from '@/pages/RequestPasswordResetPage'
import { ResetPasswordPage } from '@/pages/ResetPasswordPage'
import { GoogleOAuthCallbackPage } from '@/pages/GoogleOAuthCallbackPage'
import { UserProfilePage, USER_PROFILE_TABS } from '@/apps/users/UserProfilePage'
import { DefaultRedirect } from '@/pages/DefaultRedirect'
import { lazyResourceComponent } from '@/lib/lazyResourceComponent'
import {
  BadgeCheck,
  BadgePercent,
  Ban,
  Bot,
  Boxes,
  Building2,
  CalendarClock,
  CalendarDays,
  ClipboardCheck,
  ClipboardList,
  Columns2,
  Contact2,
  Database,
  FileClock,
  Files,
  FileText,
  FlaskConical,
  Gift,
  GitBranch,
  Globe,
  HelpCircle,
  History,
  Inbox,
  Layers,
  LayoutPanelLeft,
  ListTree,
  Lock,
  Mail,
  MapPin,
  Megaphone,
  MessageCircle,
  MessageCircleQuestion,
  MessagesSquare,
  Newspaper,
  Package,
  Percent,
  Plug,
  Radio,
  ScrollText,
  Search,
  Settings as SettingsIcon,
  ShieldCheck,
  ShoppingCart,
  Star,
  Stethoscope,
  Tags,
  Umbrella,
  UserRound,
  Users as UsersIcon,
  LayoutGrid,
  Video,
  Zap,
} from 'lucide-react'

// Each app's list/edit/create/show loads lazily, one chunk per app — see lazyResourceComponent.
const adsAudiences = {
  list: lazyResourceComponent(() => import('@/apps/ads-audiences'), (m) => m.adsAudiences.list),
}
const auditLog = {
  list: lazyResourceComponent(() => import('@/apps/audit-log'), (m) => m.auditLog.list),
  show: lazyResourceComponent(() => import('@/apps/audit-log'), (m) => m.auditLog.show),
}
const bookingsDashboardInternal = {
  list: lazyResourceComponent(
    () => import('@/apps/bookings-dashboard-internal'),
    (m) => m.bookingsDashboardInternal.list,
  ),
}
const branches = {
  list: lazyResourceComponent(() => import('@/apps/branches'), (m) => m.branches.list),
  show: lazyResourceComponent(() => import('@/apps/branches'), (m) => m.branches.show),
}
const blog = {
  list: lazyResourceComponent(() => import('@/apps/blog'), (m) => m.blog.list),
  create: lazyResourceComponent(() => import('@/apps/blog'), (m) => m.blog.create),
  edit: lazyResourceComponent(() => import('@/apps/blog'), (m) => m.blog.edit),
}
// One static-segment route per tab (HATCH-886, same shape `USER_PROFILE_TABS`
// below already established) — capitalized (unlike the `blog` object above)
// since these are used directly as JSX elements in the `CustomRoutes` block,
// not spread as a `<Resource>`'s props.
const BlogOverviewPage = lazyResourceComponent(() => import('@/apps/blog'), (m) => m.blog.overview)
const BlogCardPage = lazyResourceComponent(() => import('@/apps/blog'), (m) => m.blog.card)
const BlogArticlePage = lazyResourceComponent(() => import('@/apps/blog'), (m) => m.blog.article)
const BlogBlocksPage = lazyResourceComponent(() => import('@/apps/blog'), (m) => m.blog.blocks)
const BlogEmailPage = lazyResourceComponent(() => import('@/apps/blog'), (m) => m.blog.email)
const BlogSeoPage = lazyResourceComponent(() => import('@/apps/blog'), (m) => m.blog.seo)
const BlogBlastsPage = lazyResourceComponent(() => import('@/apps/blog'), (m) => m.blog.blasts)
const BlogActionsPage = lazyResourceComponent(() => import('@/apps/blog'), (m) => m.blog.actions)
const brandPages = {
  list: lazyResourceComponent(() => import('@/apps/brand-pages'), (m) => m.brandPages.list),
  edit: lazyResourceComponent(() => import('@/apps/brand-pages'), (m) => m.brandPages.edit),
  create: lazyResourceComponent(() => import('@/apps/brand-pages'), (m) => m.brandPages.create),
}
const brands = {
  list: lazyResourceComponent(() => import('@/apps/brands'), (m) => m.brands.list),
  show: lazyResourceComponent(() => import('@/apps/brands'), (m) => m.brands.show),
}
const holidayConfig = {
  list: lazyResourceComponent(() => import('@/apps/calendar'), (m) => m.holidayConfig.list),
  create: lazyResourceComponent(() => import('@/apps/calendar'), (m) => m.holidayConfig.create),
}
const cartBasedDiscounts = {
  list: lazyResourceComponent(() => import('@/apps/cart-based-discounts'), (m) => m.cartBasedDiscounts.list),
  edit: lazyResourceComponent(() => import('@/apps/cart-based-discounts'), (m) => m.cartBasedDiscounts.edit),
  create: lazyResourceComponent(() => import('@/apps/cart-based-discounts'), (m) => m.cartBasedDiscounts.create),
}
const categories = {
  list: lazyResourceComponent(() => import('@/apps/categories'), (m) => m.categories.list),
  edit: lazyResourceComponent(() => import('@/apps/categories'), (m) => m.categories.edit),
}
const discountCodes = {
  list: lazyResourceComponent(() => import('@/apps/discount-codes'), (m) => m.discountCodes.list),
}
const doctorProfiles = {
  list: lazyResourceComponent(() => import('@/apps/doctor-profiles'), (m) => m.doctorProfiles.list),
  edit: lazyResourceComponent(() => import('@/apps/doctor-profiles'), (m) => m.doctorProfiles.edit),
  create: lazyResourceComponent(() => import('@/apps/doctor-profiles'), (m) => m.doctorProfiles.create),
}
const doctorFaqs = {
  list: lazyResourceComponent(() => import('@/apps/doctor-faqs'), (m) => m.doctorFaqs.list),
  edit: lazyResourceComponent(() => import('@/apps/doctor-faqs'), (m) => m.doctorFaqs.edit),
  create: lazyResourceComponent(() => import('@/apps/doctor-faqs'), (m) => m.doctorFaqs.create),
}
const domains = { list: lazyResourceComponent(() => import('@/apps/domains'), (m) => m.domains.list) }
const promos = {
  list: lazyResourceComponent(() => import('@/apps/promos'), (m) => m.promos.list),
  edit: lazyResourceComponent(() => import('@/apps/promos'), (m) => m.promos.edit),
  create: lazyResourceComponent(() => import('@/apps/promos'), (m) => m.promos.create),
}
const contentManagement = {
  list: lazyResourceComponent(() => import('@/apps/content-management'), (m) => m.contentManagement.list),
  edit: lazyResourceComponent(() => import('@/apps/content-management'), (m) => m.contentManagement.edit),
  create: lazyResourceComponent(() => import('@/apps/content-management'), (m) => m.contentManagement.create),
}
const subscribers = {
  list: lazyResourceComponent(() => import('@/apps/subscribers'), (m) => m.subscribers.list),
  edit: lazyResourceComponent(() => import('@/apps/subscribers'), (m) => m.subscribers.edit),
}
const flows = { list: lazyResourceComponent(() => import('@/apps/flows'), (m) => m.flows.list) }
const dynamicForms = {
  list: lazyResourceComponent(() => import('@/apps/dynamic-forms'), (m) => m.dynamicForms.list),
  edit: lazyResourceComponent(() => import('@/apps/dynamic-forms'), (m) => m.dynamicForms.edit),
  create: lazyResourceComponent(() => import('@/apps/dynamic-forms'), (m) => m.dynamicForms.create),
}
const endorsementConfiguration = {
  list: lazyResourceComponent(() => import('@/apps/endorsement-configuration'), (m) => m.endorsementConfiguration.list),
  show: lazyResourceComponent(() => import('@/apps/endorsement-configuration'), (m) => m.endorsementConfiguration.show),
}
const faqs = {
  list: lazyResourceComponent(() => import('@/apps/faqs'), (m) => m.faqs.list),
  show: lazyResourceComponent(() => import('@/apps/faqs'), (m) => m.faqs.show),
}
const formSubmissions = {
  list: lazyResourceComponent(() => import('@/apps/form-submissions'), (m) => m.formSubmissions.list),
  show: lazyResourceComponent(() => import('@/apps/form-submissions'), (m) => m.formSubmissions.show),
}
const internalSessions = {
  list: lazyResourceComponent(() => import('@/apps/internal-sessions'), (m) => m.internalSessions.list),
}
const insuranceProviders = {
  list: lazyResourceComponent(() => import('@/apps/insurance-providers'), (m) => m.insuranceProviders.list),
}
const licenseRenewalRequests = {
  list: lazyResourceComponent(() => import('@/apps/license-renewal-requests'), (m) => m.licenseRenewalRequests.list),
}
const medicalRecordSections = {
  list: lazyResourceComponent(() => import('@/apps/medical-record-sections'), (m) => m.medicalRecordSections.list),
}
const orders = {
  list: lazyResourceComponent(() => import('@/apps/orders'), (m) => m.orders.list),
  show: lazyResourceComponent(() => import('@/apps/orders'), (m) => m.orders.show),
}
const permissions = {
  list: lazyResourceComponent(() => import('@/apps/permissions'), (m) => m.permissions.list),
  edit: lazyResourceComponent(() => import('@/apps/permissions'), (m) => m.permissions.edit),
  create: lazyResourceComponent(() => import('@/apps/permissions'), (m) => m.permissions.create),
}
const personas = { list: lazyResourceComponent(() => import('@/apps/personas'), (m) => m.personas.list) }
const products = {
  list: lazyResourceComponent(() => import('@/apps/products'), (m) => m.products.list),
  edit: lazyResourceComponent(() => import('@/apps/products'), (m) => m.products.edit),
  create: lazyResourceComponent(() => import('@/apps/products'), (m) => m.products.create),
}
const providerIntegrationsManagement = {
  list: lazyResourceComponent(
    () => import('@/apps/provider-integrations-management'),
    (m) => m.providerIntegrationsManagement.list,
  ),
  edit: lazyResourceComponent(
    () => import('@/apps/provider-integrations-management'),
    (m) => m.providerIntegrationsManagement.edit,
  ),
  create: lazyResourceComponent(
    () => import('@/apps/provider-integrations-management'),
    (m) => m.providerIntegrationsManagement.create,
  ),
}
const models = { list: lazyResourceComponent(() => import('@/apps/models'), (m) => m.models.list) }
const providers = { list: lazyResourceComponent(() => import('@/apps/providers'), (m) => m.providers.list) }
const providerTransactions = {
  list: lazyResourceComponent(() => import('@/apps/provider-transactions'), (m) => m.providerTransactions.list),
}
const ragChunks = { list: lazyResourceComponent(() => import('@/apps/rag-chunks'), (m) => m.ragChunks.list) }
const referenceSessions = {
  list: lazyResourceComponent(() => import('@/apps/reference-sessions'), (m) => m.referenceSessions.list),
}
const reviews = { list: lazyResourceComponent(() => import('@/apps/reviews'), (m) => m.reviews.list) }
const ragCollections = {
  list: lazyResourceComponent(() => import('@/apps/rag-collections'), (m) => m.ragCollections.list),
}
const schemas = { list: lazyResourceComponent(() => import('@/apps/schemas'), (m) => m.schemas.list) }
const schemaEvaluations = {
  list: lazyResourceComponent(() => import('@/apps/schema-evaluations'), (m) => m.schemaEvaluations.list),
}
const schemasDetail = {
  edit: lazyResourceComponent(() => import('@/apps/schemas-detail'), (m) => m.schemasDetail.edit),
  create: lazyResourceComponent(() => import('@/apps/schemas-detail'), (m) => m.schemasDetail.create),
}
const sections = {
  list: lazyResourceComponent(() => import('@/apps/sections'), (m) => m.sections.list),
  show: lazyResourceComponent(() => import('@/apps/sections'), (m) => m.sections.show),
}
const seoManager = { list: lazyResourceComponent(() => import('@/apps/seo-manager'), (m) => m.seoManager.list) }
const settings = { list: lazyResourceComponent(() => import('@/apps/settings'), (m) => m.settings.list) }
const sessionCompare = {
  list: lazyResourceComponent(() => import('@/apps/session-compare'), (m) => m.sessionCompare.list),
}
const sessionList = { list: lazyResourceComponent(() => import('@/apps/session-list'), (m) => m.sessionList.list) }
const specialistsCalendar = {
  list: lazyResourceComponent(() => import('@/apps/specialists-calendar'), (m) => m.specialistsCalendar.list),
}
const specialistsMonitor = {
  list: lazyResourceComponent(() => import('@/apps/specialists-monitor'), (m) => m.specialistsMonitor.list),
}
const specialistsReview = {
  list: lazyResourceComponent(() => import('@/apps/specialists-review'), (m) => m.specialistsReview.list),
  edit: lazyResourceComponent(() => import('@/apps/specialists-review'), (m) => m.specialistsReview.edit),
}
const triggers = {
  list: lazyResourceComponent(() => import('@/apps/triggers'), (m) => m.triggers.list),
  edit: lazyResourceComponent(() => import('@/apps/triggers'), (m) => m.triggers.edit),
  create: lazyResourceComponent(() => import('@/apps/triggers'), (m) => m.triggers.create),
}
const videos = {
  list: lazyResourceComponent(() => import('@/apps/videos'), (m) => m.videos.list),
  edit: lazyResourceComponent(() => import('@/apps/videos'), (m) => m.videos.edit),
  create: lazyResourceComponent(() => import('@/apps/videos'), (m) => m.videos.create),
}
const viewSingleSession = {
  list: lazyResourceComponent(() => import('@/apps/view-single-session'), (m) => m.viewSingleSession.list),
}
const userSessions = {
  list: lazyResourceComponent(() => import('@/apps/user-sessions'), (m) => m.userSessions.list),
}
const users = {
  list: lazyResourceComponent(() => import('@/apps/users'), (m) => m.users.list),
  edit: lazyResourceComponent(() => import('@/apps/users'), (m) => m.users.edit),
  create: lazyResourceComponent(() => import('@/apps/users'), (m) => m.users.create),
  show: lazyResourceComponent(() => import('@/apps/users'), (m) => m.users.show),
}

function App() {
  return (
    <BrowserRouter>
      <Admin
        dataProvider={dataProvider}
        authProvider={authProvider}
        loginPage={SignInPage}
        loading={Loading}
        requireAuth
        title="Marketplace Admin"
      >
        <CustomRoutes noLayout>
          <Route path="/request-password-reset" element={<RequestPasswordResetPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          {/* Google's OAuth redirect_uri points here; ra-core's own auth-callback
          handling is hardcoded to /auth-callback, so this route is what actually
          receives Google's redirect (see GoogleOAuthCallbackPage for why it can't
          just reuse ra-core's AuthCallback/useHandleAuthCallback). */}
          <Route path="/oauth/google/callback" element={<GoogleOAuthCallbackPage />} />
        </CustomRoutes>
        {/* HATCH-101 round 4: one static-segment route per tab (not a single
        `/users/:id/:tab` dynamic route) — a dynamic `:tab` segment would
        out-score and shadow the `users` resource's own `:id/show` route
        below for any URL matching `/users/:id/show` (react-router resolves
        by specificity across ALL sibling routes, not JSX order), since
        `:tab` would happily capture the literal "show". Static segments
        below can't collide with that literal. */}
        <CustomRoutes>
          {USER_PROFILE_TABS.map((tab) => (
            <Route key={tab} path={`/users/:id/${tab}`} element={<UserProfilePage tab={tab} />} />
          ))}
        </CustomRoutes>
        {/* HATCH-672: scope-aware landing page. This "/" route sits in a
        CustomRoutes block declared before the Resources below, so it wins
        the tie against react-admin's own internal "/" fallback (equal
        specificity resolves by declaration order, per the note above) —
        without passing an Admin `dashboard` prop, which would also add an
        unwanted "Dashboard" sidebar entry for every user. */}
        <CustomRoutes>
          <Route path="/" element={<DefaultRedirect />} />
        </CustomRoutes>
        {/* Backoffice nav category (HATCH-111) */}
        <Resource name="orders" {...orders} icon={ShoppingCart} options={{ category: 'Backoffice' }} />
        <Resource
          name="specialists-review"
          {...specialistsReview}
          icon={Stethoscope}
          options={{ category: 'Backoffice', label: 'Specialists Reviews' }}
        />
        {/* HATCH-557: same "onboarding approval queue" shape as
        `specialists-review` above, but over pending license-renewal
        submissions instead of new-profile submissions. */}
        <Resource
          name="license-renewal-requests"
          {...licenseRenewalRequests}
          icon={FileClock}
          options={{ category: 'Backoffice', label: 'License Renewals' }}
        />
        {/* HATCH-606: the prescription-blocked transactions queue, same
        "admin review queue" shape as `license-renewal-requests` above. */}
        <Resource
          name="provider-transactions"
          {...providerTransactions}
          icon={Ban}
          options={{ category: 'Backoffice', label: 'Prescription-Blocked Transactions' }}
        />
        <Resource name="reviews" {...reviews} icon={Star} options={{ category: 'Backoffice' }} />
        <Resource name="users" {...users} icon={UsersIcon} options={{ category: 'Backoffice' }} />
        <Resource
          name="bookings-dashboard-internal"
          {...bookingsDashboardInternal}
          icon={ClipboardList}
          options={{ category: 'Backoffice', label: 'Bookings Dashboard' }}
        />
        {/* HATCH-1080: live "who's online now" monitor, distinct from
        `specialists-review`'s onboarding queue above. */}
        <Resource
          name="specialists-monitor"
          {...specialistsMonitor}
          icon={Radio}
          options={{ category: 'Backoffice', label: 'Online Specialists' }}
        />
        {/* Scheduling nav category (HATCH-120, Tier 3 §7) — the bookable
        `events` calendar layer is descoped for now (2026-07-28 review round
        2, see `apps/calendar/MIGRATION-NOTES.md`), so `holiday-config` is
        the calendar's own primary (and only) resource. */}
        <Resource
          name="holiday-config"
          {...holidayConfig}
          icon={CalendarDays}
          options={{ category: 'Scheduling', label: 'Holiday Calendar' }}
        />
        {/* HATCH-154 (ticket-comment re-scope): a doctor-picker page over the
        shared `SpecialistCalendar` component, distinct from `holiday-config`'s
        own one-off unavailability calendar above. */}
        <Resource
          name="specialists-calendar"
          {...specialistsCalendar}
          icon={CalendarClock}
          options={{ category: 'Scheduling', label: 'Specialists Calendar' }}
        />
        {/* Storefront nav category (HATCH-111) */}
        <Resource name="products" {...products} icon={Package} options={{ category: 'Storefront' }} />
        <Resource name="sections" {...sections} icon={LayoutGrid} options={{ category: 'Storefront' }} />
        <Resource name="categories" {...categories} icon={Tags} options={{ category: 'Storefront' }} />
        <Resource name="brands" {...brands} icon={BadgeCheck} options={{ category: 'Storefront' }} />
        <Resource
          name="brand-pages"
          {...brandPages}
          icon={FileText}
          options={{ category: 'Storefront', label: 'Brand Pages' }}
        />
        <Resource name="branches" {...branches} icon={MapPin} options={{ category: 'Storefront' }} />
        <Resource
          name="doctor-profiles"
          {...doctorProfiles}
          icon={UserRound}
          options={{ category: 'Storefront', label: 'Doctor Profiles' }}
        />
        <Resource name="faqs" {...faqs} icon={HelpCircle} options={{ category: 'Storefront', label: 'FAQs' }} />
        {/* Personas (this ticket): the never-ported second screen of the
        same source export `faqs` (which only needed personas as a
        read-only reference field) came from — grouped next to FAQs, the
        one existing app that already references this resource. */}
        <Resource name="personas" {...personas} icon={Contact2} options={{ category: 'Storefront' }} />
        {/* Marketing nav category (HATCH-111) */}
        {/* Videos (HATCH-501): short-form video content (Cloudflare Stream) — a brand-new backend feature, no source Retool app. */}
        <Resource name="videos" {...videos} icon={Video} options={{ category: 'Marketing' }} />
        {/* Blog (HATCH-644): list + create-brief only — a brand-new backend
        feature, no source Retool app. Editing/generation/publish-unpublish
        land in follow-up tickets this one blocks. */}
        <Resource name="blogs" {...blog} icon={Newspaper} options={{ category: 'Marketing', label: 'Blog' }} />
        {/* HATCH-886/HATCH-892: the detail page's Overview/Card/Article/Email/
        SEO/Actions tabs each get their own URL — see `apps/blog`'s
        `BlogDetailPage` doc comment. */}
        <CustomRoutes>
          <Route path="/blogs/:id/overview" element={<BlogOverviewPage />} />
          <Route path="/blogs/:id/card" element={<BlogCardPage />} />
          <Route path="/blogs/:id/article" element={<BlogArticlePage />} />
          <Route path="/blogs/:id/blocks" element={<BlogBlocksPage />} />
          <Route path="/blogs/:id/email" element={<BlogEmailPage />} />
          <Route path="/blogs/:id/seo" element={<BlogSeoPage />} />
          <Route path="/blogs/:id/blasts" element={<BlogBlastsPage />} />
          <Route path="/blogs/:id/actions" element={<BlogActionsPage />} />
        </CustomRoutes>
        <Resource
          name="discount-codes"
          {...discountCodes}
          icon={Percent}
          options={{ category: 'Marketing', label: 'Discount Codes' }}
        />
        {/* Ticket suggested a "Catalog" category alongside products/pricing/
        discount-codes, but no such category exists in this nav yet (only
        `Storefront` and `Marketing`, see the categories above) — grouped
        under `Marketing` next to `discount-codes` as the closest existing
        analog; confirm at review time (HATCH-140). */}
        <Resource
          name="cart-discounts"
          {...cartBasedDiscounts}
          icon={BadgePercent}
          options={{ category: 'Marketing', label: 'Cart-based Discounts' }}
        />
        <Resource
          name="ads-audiences"
          {...adsAudiences}
          icon={Megaphone}
          options={{ category: 'Marketing', label: 'Ads Audiences' }}
        />
        {/* Promos (HATCH-781): storefront countdown-style promos
        (`v2/admin/content/promos`) — a brand-new backend feature, no source
        Retool app. Ticket says "under marketing", grouped here next to the
        other Marketing-category apps. `name` is a real required field on
        every record, so `recordRepresentation` keeps the edit page's
        header/breadcrumb showing the promo's name instead of the raw
        `promo_id` uuid. */}
        <Resource name="promos" {...promos} icon={Gift} recordRepresentation="name" options={{ category: 'Marketing' }} />
        {/* HTML Content (HATCH-1061): ported from `marketplace-frontend`'s
        own `/admin/content` screen — list+edit+create over the `html-content`
        resource, already used elsewhere in this app as a reference/embed
        target (`apps/triggers`' `action_content_id`, each `apps/blog`
        article) but never its own first-class screen until now. Labeled
        "Single Sends" (HATCH-1243), not "Content" — the list itself is
        permanently scoped to `content_type: 'single_send'` since blogs and
        program-stage content are both managed by their own dedicated apps. */}
        <Resource
          name="html-content"
          {...contentManagement}
          icon={Files}
          recordRepresentation="name"
          options={{ category: 'Marketing', label: 'Single Sends' }}
        />
        {/* Subscribers (this ticket): the email/newsletter mailing-list
        opt-in table (`v2/admin/subscribers`) — grouped under Marketing next
        to the other list-building apps (`ads-audiences`, `discount-codes`).
        `recordRepresentation="email"` (round 4 review: "use in breadcrumbs
        and the title of the page email instead of subscriber id") keeps
        the detail page's header/breadcrumb showing the subscriber's email
        instead of the raw `subscriber_id` uuid, same as `promos`/
        `doctor-faqs`' own `recordRepresentation` above. */}
        <Resource
          name="subscribers"
          {...subscribers}
          icon={Mail}
          recordRepresentation="email"
          options={{ category: 'Marketing' }}
        />
        {/* Providers nav category (HATCH-111) */}
        <Resource name="providers" {...providers} icon={Building2} options={{ category: 'Providers' }} />
        <Resource
          name="provider-integrations"
          {...providerIntegrationsManagement}
          icon={Plug}
          options={{ category: 'Providers', label: 'Integrations Config' }}
        />
        <Resource
          name="endorsement-configs"
          {...endorsementConfiguration}
          icon={ShieldCheck}
          options={{ category: 'Providers', label: 'Endorsement Config' }}
        />
        <Resource
          name="insurance-providers"
          {...insuranceProviders}
          icon={Umbrella}
          options={{ category: 'Providers', label: 'Insurance Providers' }}
        />
        {/* Administration nav category (HATCH-668 follow-up) */}
        <Resource name="domains" {...domains} icon={Globe} options={{ category: 'Administration' }} />
        <Resource
          name="seo"
          {...seoManager}
          icon={Search}
          options={{ category: 'Administration', label: 'SEO Manager' }}
        />
        {/* Settings (this ticket): the true-singleton `v2/admin/settings`
        endpoint (enabled payment methods, iOS/Android min app version) — a
        single page, not a list (see `apps/settings`'s own doc comment). */}
        <Resource name="settings" {...settings} icon={SettingsIcon} options={{ category: 'Administration' }} />
        <Resource name="triggers" {...triggers} icon={Zap} options={{ category: 'Administration' }} />
        <Resource
          name="dynamic-forms"
          {...dynamicForms}
          icon={ClipboardList}
          options={{ category: 'Administration', label: 'Dynamic Forms' }}
        />
        <Resource
          name="form-submissions"
          {...formSubmissions}
          icon={Inbox}
          options={{ category: 'Administration', label: 'Form Submissions' }}
        />
        <Resource
          name="medical-record-sections"
          {...medicalRecordSections}
          icon={ListTree}
          options={{ category: 'Administration', label: 'Medical Record Sections' }}
        />
        {/* Specialist FAQs (HATCH-726): FAQ content shown to doctors — a
        brand-new backend feature (`v2/admin/content/doctor-faqs`), a
        separate resource from `faqs` above (no product/persona scoping).
        Review feedback (round 2): grouped under Administration (not
        Storefront) and labeled "Specialist FAQs", not "Doctor FAQs" — the
        resource name/API path stays `doctor-faqs` either way. `question` is
        a real required field on every record, so `recordRepresentation`
        (a plain field-name string, same as react-admin's own docs example)
        is enough to keep the edit page's header/breadcrumb showing the
        question text instead of the raw `doctor_faq_id` uuid. */}
        <Resource
          name="doctor-faqs"
          {...doctorFaqs}
          icon={MessageCircleQuestion}
          recordRepresentation="question"
          options={{ category: 'Administration', label: 'Specialist FAQs' }}
        />
        {/* Security nav category (HATCH-111) */}
        <Resource name="permissions" {...permissions} icon={Lock} options={{ category: 'Security' }} />
        <Resource
          name="audit-log"
          {...auditLog}
          icon={ScrollText}
          options={{ category: 'Security', label: 'Audit Log' }}
        />
        {/* Chat bot nav category (HATCH-567 created it; HATCH-568/569, HATCH-573,
        HATCH-306, HATCH-574, HATCH-575, HATCH-579, and this ticket each add
        another app to it — `schemas`, `schemas-detail`, `rag-chunks`,
        `flows`, `rag-collections`, and this ticket's `models` are the RAG/AI
        admin cluster, `migration-manifest.yaml`'s
        `schemas`/`schemas-detail`/`rag-collections`/`models`/etc. note). */}
        <Resource
          name="view-single-session"
          {...viewSingleSession}
          icon={MessageCircle}
          options={{ category: 'Chat bot', label: 'Browse by ID' }}
        />
        <Resource
          name="session-list"
          {...sessionList}
          icon={MessagesSquare}
          options={{ category: 'Chat bot', label: 'Session List' }}
        />
        <Resource
          name="user-sessions"
          {...userSessions}
          icon={History}
          options={{ category: 'Chat bot', label: 'User Sessions' }}
        />
        {/* Internal Sessions (this ticket, HATCH-570) — a multi-panel review
        tool over the bot's internal (Retool-test/QA) channels, embedding the
        same shared `SessionViewer` primitive as the page above. */}
        <Resource
          name="internal-sessions"
          {...internalSessions}
          icon={LayoutPanelLeft}
          options={{ category: 'Chat bot', label: 'Internal Sessions' }}
        />
        <Resource name="schemas" {...schemas} icon={Bot} options={{ category: 'Chat bot' }} />
        <Resource
          name="rag-chunks"
          {...ragChunks}
          icon={Layers}
          options={{ category: 'Chat bot', label: 'RAG Chunks' }}
        />
        <Resource
          name="rag-collections"
          {...ragCollections}
          icon={Database}
          options={{ category: 'Chat bot', label: 'RAG Collections' }}
        />
        {/* HATCH-574: `apps/schemas`' own companion detail/edit page —
        `edit`/`create` only, no `list`, so `hasList` is false and
        `AppSidebar` (which builds its menu from `hasList`,
        `packages/ui-kit/components/admin/app-sidebar.tsx`) never lists it as
        a separate nav destination (PR #305 review, Alex Gridnev: it's a
        detail page for `schemas`, not its own thing). Reached only via the
        `Edit`/`New schema` links `apps/schemas` itself renders. */}
        <Resource name="schemas-detail" {...schemasDetail} />
        <Resource name="flows" {...flows} icon={GitBranch} options={{ category: 'Chat bot', label: 'Chat Flows' }} />
        <Resource name="models" {...models} icon={Boxes} options={{ category: 'Chat bot' }} />
        <Resource
          name="session-compare"
          {...sessionCompare}
          icon={Columns2}
          options={{ category: 'Chat bot', label: 'Session Comparison' }}
        />
        <Resource
          name="reference-sessions"
          {...referenceSessions}
          icon={ClipboardCheck}
          options={{ category: 'Chat bot', label: 'Reference Sessions' }}
        />
        <Resource
          name="schema-evaluations"
          {...schemaEvaluations}
          icon={FlaskConical}
          options={{ category: 'Chat bot', label: 'Schema Evaluations' }}
        />
      </Admin>
    </BrowserRouter>
  )
}

export default App
